import { Stage } from "./CaseConfiguration.ts";

/** A date as the user enters it, without a time: `yyyy-mm-dd`. */
export type IsoDate = string;

/**
 * The dates of a case and the stages as the user enters them; nothing of it
 * is assumed (docs/InsurerReserves.md, step 2). A stage has a duration the
 * user expects it to take, in whole months, and the date it ended once it is
 * closed; a stage closed before the timeline existed is closed without one
 * until the user enters it. A stage a settlement left unreached is closed as
 * `settled` (see settledTimeline). The dates of loss and of notification are
 * an insurer's.
 */
export interface Timeline {
  start?: IsoDate;
  lossDate?: IsoDate;
  notifiedDate?: IsoDate;
  stages?: {
    [stage in Stage]?: {
      months?: number;
      ended?: IsoDate;
      closed?: boolean;
      settled?: boolean;
    };
  };
}

/** Where a stage stands on the timeline. */
export type StageStatus =
  // it ended
  | "closed"
  // the first stage not ended, within its expected end or without one
  | "open"
  // the first stage not ended, past its expected end
  | "overdue"
  // to come, with an expected end
  | "planned"
  // to come, with an expected end that follows an overdue stage
  | "afterOverdue"
  // to come, without an expected end
  | "notPlanned";

export type StageOnTimeline = {
  stage: Stage;
  // closed, with the date it ended or without
  months?: number;
  // the end of the stage before, or the start: where its duration counts from
  begins?: IsoDate;
  expectedEnd?: IsoDate;
  ended?: IsoDate;
  status: StageStatus;
};

const parse = (date: IsoDate) => {
  const [year, month, day] = date.split("-").map(Number);
  return { year, month, day };
};

const format = (year: number, month: number, day: number): IsoDate =>
  `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

/**
 * A date some whole months on: the same day of the month, or the last day of
 * a month too short for it (31 January and a month is 28 or 29 February).
 */
export function addMonths(date: IsoDate, months: number): IsoDate {
  const { year, month, day } = parse(date);
  const index = year * 12 + (month - 1) + months;
  const toYear = Math.floor(index / 12);
  const toMonth = (index % 12) + 1;
  // day 0 of the next month is the last of this one
  const lastDay = new Date(Date.UTC(toYear, toMonth, 0)).getUTCDate();
  return format(toYear, toMonth, Math.min(day, lastDay));
}

/**
 * Whether a stage has ended: on a date the user entered, before the timeline
 * existed, or unreached once a settlement ended the case.
 */
export const isClosed = (
  timeline: Timeline | undefined,
  stage: Stage,
): boolean => {
  const of = timeline?.stages?.[stage];
  return of?.ended !== undefined || of?.closed === true || of?.settled === true;
};

/** A duration as the user can enter it: whole months, none negative. */
export const isMonths = (value: unknown): value is number =>
  typeof value === "number" && Number.isInteger(value) && value >= 0;

/**
 * The stages of a case on its timeline, in their order: when each is expected
 * to end and where it stands on `today`. A stage is expected to end its
 * duration after the end of the stage before, or after the start for the
 * first; without a duration, or without a date to count from, it has no
 * expected end, nor has a stage that counts from it. Expected ends never move
 * by themselves: an open stage past its own is overdue, and those after it
 * keep theirs.
 */
export function stagesOnTimeline(
  timeline: Timeline | undefined,
  stages: Stage[],
  today: IsoDate,
): StageOnTimeline[] {
  const rows: StageOnTimeline[] = [];
  let begins = timeline?.start;
  let openFound = false;
  let overdue = false;
  for (const stage of stages) {
    const { months, ended } = timeline?.stages?.[stage] ?? {};
    const closed = isClosed(timeline, stage);
    const expectedEnd =
      begins !== undefined && isMonths(months)
        ? addMonths(begins, months)
        : undefined;
    let status: StageStatus;
    if (closed) status = "closed";
    else if (!openFound) {
      openFound = true;
      overdue = expectedEnd !== undefined && expectedEnd < today;
      status = overdue ? "overdue" : "open";
    } else if (expectedEnd === undefined) status = "notPlanned";
    else status = overdue ? "afterOverdue" : "planned";
    rows.push({
      stage,
      ...(isMonths(months) && { months }),
      ...(begins !== undefined && { begins }),
      ...(expectedEnd !== undefined && { expectedEnd }),
      ...(ended !== undefined && { ended }),
      status,
    });
    // a stage closed without a date gives the next none to count from
    begins = closed ? ended : expectedEnd;
  }
  return rows;
}

/** The stage that is open: the first not ended; none once all have. */
export const openStageOf = (
  timeline: Timeline | undefined,
  stages: Stage[],
): Stage | undefined => stages.find((stage) => !isClosed(timeline, stage));

/**
 * The stage a case is at: the open stage; the one it settled in once a
 * settlement ended it; the last once every stage has ended.
 */
export const currentStageOf = (
  timeline: Timeline | undefined,
  stages: Stage[],
): Stage =>
  openStageOf(timeline, stages) ??
  settledStageOf(timeline, stages) ??
  stages[stages.length - 1];

/** The stage a settlement ended the case in: the one before the first it left unreached. */
export function settledStageOf(
  timeline: Timeline | undefined,
  stages: Stage[],
): Stage | undefined {
  const first = stages.findIndex((stage) => timeline?.stages?.[stage]?.settled);
  return first > 0 ? stages[first - 1] : undefined;
}

/**
 * The timeline as a settlement leaves it: without `ending`, the stages an
 * earlier one closed open again, their durations kept; with it, its stage
 * ended on its day, or closed without one, and every stage after it
 * unreached. A date the user entered for an undated end stays.
 */
export function settledTimeline(
  timeline: Timeline | undefined,
  stages: Stage[],
  ending?: { stage: Stage; on?: IsoDate },
): Timeline {
  const before = settledStageOf(timeline, stages);
  const entered =
    before !== undefined && before === ending?.stage
      ? timeline?.stages?.[before]?.ended
      : undefined;
  const at = ending ? stages.indexOf(ending.stage) : -1;
  const settledStages = { ...timeline?.stages };
  stages.forEach((stage, index) => {
    const { months } = settledStages[stage] ?? {};
    const kept = months === undefined ? {} : { months };
    const ended = ending?.on ?? entered;
    const reopened =
      before !== undefined &&
      (stage === before || !!settledStages[stage]?.settled);
    if (ending && index > at) settledStages[stage] = { ...kept, settled: true };
    else if (ending && index === at)
      settledStages[stage] = ended
        ? { ...kept, ended }
        : { ...kept, closed: true };
    else if (reopened && months === undefined) delete settledStages[stage];
    else if (reopened) settledStages[stage] = kept;
  });
  return { ...timeline, stages: settledStages };
}

/**
 * The stage closed last, which reopening opens again: the one before the open
 * one; none once a settlement ended the case, which changing the settlement
 * undoes.
 */
export function lastClosedStageOf(
  timeline: Timeline | undefined,
  stages: Stage[],
): Stage | undefined {
  if (settledStageOf(timeline, stages) !== undefined) return undefined;
  const open = openStageOf(timeline, stages);
  const at = open === undefined ? stages.length : stages.indexOf(open);
  if (at === 0 || !isClosed(timeline, stages[at - 1])) return undefined;
  const last = stages[at - 1];
  return isPassedOver(timeline, last) ? Stage.Trial : last;
}

/**
 * Whether a stage was passed over: Appeals, closing Trial without an appeal;
 * any stage a settlement left unreached.
 */
export const isPassedOver = (
  timeline: Timeline | undefined,
  stage: Stage,
): boolean =>
  !!timeline?.stages?.[stage]?.settled ||
  (stage === Stage.Appeals &&
    isClosed(timeline, stage) &&
    !timeline?.stages?.[stage]?.ended &&
    !!timeline?.stages?.[Stage.Trial]?.ended);

/** The timeline with a stage open again: no longer ended, its duration kept. */
export function withReopened(
  timeline: Timeline | undefined,
  stage: Stage,
): Timeline {
  const { ended: _, closed: __, ...rest } = timeline?.stages?.[stage] ?? {};
  const stages = { ...timeline?.stages, [stage]: rest };
  // what closing the stage passed over comes back with it
  if (stage === Stage.Trial && isPassedOver(timeline, Stage.Appeals)) {
    const { closed: ___, ...appeals } = stages[Stage.Appeals] ?? {};
    stages[Stage.Appeals] = appeals;
  }
  return { ...timeline, stages };
}

/**
 * The timeline of a case from before there was one, as its costs say where
 * it stands: the stages before `current` closed, without a date, since none
 * was entered; nothing else of it is known.
 */
export function timelineClosedBefore(
  stages: Stage[],
  current: Stage,
): Timeline {
  return {
    stages: Object.fromEntries(
      stages
        .filter((stage) => stage < current)
        .map((stage) => [stage, { closed: true }]),
    ),
  };
}

/** The timeline with a stage's duration, or without one where `months` is undefined. */
export function withMonths(
  timeline: Timeline | undefined,
  stage: Stage,
  months: number | undefined,
): Timeline {
  const { months: _, ...rest } = timeline?.stages?.[stage] ?? {};
  return {
    ...timeline,
    stages: {
      ...timeline?.stages,
      [stage]: months === undefined ? rest : { ...rest, months },
    },
  };
}

/** The timeline with a stage ended on a date. */
export const withEnded = (
  timeline: Timeline | undefined,
  stage: Stage,
  ended: IsoDate,
): Timeline => {
  // the date says it is closed: a flag of closing without one goes
  const { closed: _, ...rest } = timeline?.stages?.[stage] ?? {};
  return {
    ...timeline,
    stages: { ...timeline?.stages, [stage]: { ...rest, ended } },
  };
};

/** The timeline with a stage passed over: closed, without a date. */
export const withPassedOver = (
  timeline: Timeline | undefined,
  stage: Stage,
): Timeline => ({
  ...timeline,
  stages: {
    ...timeline?.stages,
    [stage]: { ...timeline?.stages?.[stage], closed: true },
  },
});

/**
 * The days a stage can have ended on: not before the start of the case or the
 * end of a stage before it, not after the end of a stage after it or today.
 */
export function endDateRange(
  timeline: Timeline | undefined,
  stages: Stage[],
  stage: Stage,
  today: IsoDate,
): { min?: IsoDate; max: IsoDate } {
  const at = stages.indexOf(stage);
  const endsOf = (some: Stage[]) =>
    some.flatMap((s) => timeline?.stages?.[s]?.ended ?? []).sort();
  const before = [
    ...(timeline?.start ? [timeline.start] : []),
    ...endsOf(stages.slice(0, at)),
  ].sort();
  return {
    min: before[before.length - 1],
    max: endsOf(stages.slice(at + 1))[0] ?? today,
  };
}

/** A date as the user reads it. */
export const showDate = (date: IsoDate): string =>
  new Date(`${date}T00:00:00`).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

/** Today as a date, in the user's time zone. */
export function todayIso(now: Date = new Date()): IsoDate {
  return format(now.getFullYear(), now.getMonth() + 1, now.getDate());
}
